// Period comparison for the КУДИР stats chart: each bucket of the (gap-filled, chronological)
// timeline is paired with the bucket `offset` steps before it — 1 for the neighbouring period,
// a full year of buckets (12 months / 4 quarters) for the same period of the previous year.

export interface CompareRow {
  bucket: string;
  label: string;
  current: number;
  // null for the first `offset` buckets of the timeline — there is nothing to compare them to.
  prev: number | null;
  prevLabel: string | null;
  delta: number | null;
  // null when there is no previous bucket or the previous value is 0 (growth from zero has no %).
  deltaPct: number | null;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function buildCompareData<T extends { bucket: string; label: string }>(
  rows: T[],
  value: (row: T) => number,
  offset = 1
): CompareRow[] {
  return rows.map((row, i) => {
    const current = value(row);
    if (i < offset) {
      return { bucket: row.bucket, label: row.label, current, prev: null, prevLabel: null, delta: null, deltaPct: null };
    }
    const prev = value(rows[i - offset]);
    return {
      bucket: row.bucket,
      label: row.label,
      current,
      prev,
      prevLabel: rows[i - offset].label,
      delta: round2(current - prev),
      deltaPct: prev === 0 ? null : Math.round(((current - prev) / prev) * 1000) / 10,
    };
  });
}

// How many buckets make a year, i.e. the offset to the same period of the previous year.
export function yearOffset(groupBy: 'month' | 'quarter' | 'year'): number {
  return groupBy === 'month' ? 12 : groupBy === 'quarter' ? 4 : 1;
}

// '+12.5%' / '−8%' / '0%'; '' when there is no percentage to show.
export function formatDeltaPct(pct: number | null): string {
  if (pct === null) return '';
  if (pct === 0) return '0%';
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`;
}
